import React, { lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";

import OutsideClickComponent from "../../fragments/OutsideClickComponent";
import { isLightColor } from "../../../lib/script";
import { useCanEditArchived, useUserTabEditPermissionDisabled } from "../../../lib/usersPermissions";
import { WorkQHoverHint } from "../context/workQShellContext";
import { useWorkQEditSession } from "../../../lib/hooks/useWorkQEditSession";

import classes from "../../common/interactive/CustomSelectCopy.module.css";
import shellClasses from "./WorkQDeferredCustomSelect.module.css";

const LazyCustomSelect = lazy(() => import("../../common/interactive/CustomSelect_copy"));

const defaultShouldCollapse = () => true;
const toBkColorClass = (rawColor) => {
    if (!rawColor || typeof rawColor !== "string") {
        return "";
    }
    const normalized = rawColor.split(" ").join("_");
    return normalized ? `bk_${normalized}` : "";
};

function WorkQDeferredCustomSelect(props) {
    const {
        hoverMessage,
        hoverDisabled,
        shouldCollapseAfterAction = defaultShouldCollapse,
        getOptionsData,
        optionsData: optionsDataProp,
        preSelectedValue,
        fallbackSelectedOption,
        action,
        label,
        undetermined,
        isMismatched,
        minWidth,
        isDisabled,
        isLoading,
        identifier,
        thin_select,
        bkTransparent,
        borderRed,
        openOnShellClick = true,
        focusOnMount: _focusOnMountProp,
        ...restSelectProps
    } = props;

    const [editing, setEditing] = useState(false);
    const ignoreOpenRef = useRef(false);
    const ignoreOpenClearRef = useRef(null);
    useWorkQEditSession(editing);

    const armIgnoreOpen = useCallback(() => {
        ignoreOpenClearRef.current?.();
        ignoreOpenRef.current = true;
        let timerId = null;
        const clear = () => {
            ignoreOpenRef.current = false;
            document.removeEventListener("click", clear, false);
            if (timerId != null) {
                window.clearTimeout(timerId);
                timerId = null;
            }
            if (ignoreOpenClearRef.current === clear) {
                ignoreOpenClearRef.current = null;
            }
        };
        ignoreOpenClearRef.current = clear;
        // Bubble phase runs after the shell's click handler, so this gesture cannot reopen.
        document.addEventListener("click", clear, false);
        timerId = window.setTimeout(clear, 300);
    }, []);

    useEffect(() => {
        return () => {
            ignoreOpenClearRef.current?.();
        };
    }, []);

    const resolvedOptions = useMemo(() => {
        if (optionsDataProp != null) {
            return optionsDataProp;
        }
        if (typeof getOptionsData === "function") {
            return getOptionsData();
        }
        return null;
    }, [optionsDataProp, getOptionsData]);

    const selectedOption = useMemo(() => {
        if (!resolvedOptions?.length) {
            return null;
        }
        const found = resolvedOptions.find((item) => item.value == preSelectedValue) ?? null;
        return found;
    }, [resolvedOptions, preSelectedValue]);

    const fallbackResolvedSelection = useMemo(() => {
        if (!fallbackSelectedOption) return null;
        if (preSelectedValue == null || preSelectedValue === "") return null;
        try {
            return fallbackSelectedOption(preSelectedValue) ?? null;
        } catch {
            return null;
        }
    }, [fallbackSelectedOption, preSelectedValue]);

    let shellLabel = "";
    if (selectedOption) {
        shellLabel = selectedOption.label;
    } else if (undetermined && (preSelectedValue === "" || preSelectedValue == null)) {
        shellLabel = undetermined;
    } else if (preSelectedValue != null && preSelectedValue !== "") {
        shellLabel = fallbackResolvedSelection?.label ?? String(preSelectedValue);
    } else if (undetermined) {
        shellLabel = undetermined;
    } else {
        shellLabel = "—";
    }

    const bkColor = selectedOption?.color_code;
    const textColor = bkColor ? (isLightColor(bkColor) ? "var(--text)" : "var(--white)") : undefined;
    const colorClass = toBkColorClass(selectedOption?.color);
    const shouldUseColorCode = Boolean(bkColor);
    const shellBkColor = shouldUseColorCode ? bkColor : undefined;
    const shellTextColor = shouldUseColorCode ? textColor : undefined;
    const shellColorClass = shouldUseColorCode ? "" : colorClass;

    const editArchivedDisabled = useCanEditArchived();
    const userPermissionDisabled = useUserTabEditPermissionDisabled(
        identifier?.permissionType ? identifier?.permissionType : identifier?.type,
    );
    const isSelectDisabled = Boolean(isDisabled || isLoading || editArchivedDisabled || userPermissionDisabled);
    const wrappedAction = useCallback(
        (payload) => {
            action?.(payload);
            if (shouldCollapseAfterAction(payload)) {
                queueMicrotask(() => setEditing(false));
            }
        },
        [action, shouldCollapseAfterAction],
    );

    const enterEdit = useCallback(() => {
        if (ignoreOpenRef.current || isSelectDisabled) {
            return;
        }
        setEditing(true);
    }, [isSelectDisabled]);

    const exitEdit = useCallback(() => {
        armIgnoreOpen();
        queueMicrotask(() => setEditing(false));
    }, [armIgnoreOpen]);

    useEffect(() => {
        if (!editing) {
            return undefined;
        }
        const onKey = (e) => {
            if (e.key === "Escape") {
                e.stopPropagation();
                setEditing(false);
            }
        };
        document.addEventListener("keydown", onKey, true);
        return () => document.removeEventListener("keydown", onKey, true);
    }, [editing]);

    const shouldOpenOnShellClick = openOnShellClick && !isSelectDisabled;

    const shell = (
        <div
            className={`${classes.same_size_as_container} ${isMismatched ? classes.is_mismatched : ""} ${
                !label ? classes.no_label_container : ""
            } ${thin_select ? classes.thin_select : ""} ${isSelectDisabled ? classes.is_inactive : ""}`}
            style={{ minWidth: minWidth ? minWidth : "", width: "100%" }}
            data-workq-select-shell="true"
            role={shouldOpenOnShellClick ? "button" : undefined}
            tabIndex={shouldOpenOnShellClick ? 0 : undefined}
            onClick={shouldOpenOnShellClick ? enterEdit : undefined}
            onKeyDown={
                shouldOpenOnShellClick
                    ? (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              enterEdit();
                          }
                      }
                    : undefined
            }
        >
            <div
                    className={`${classes.input_container} ${shellColorClass} ${bkTransparent ? classes.background_transparent : ""} ${
                    borderRed ? classes.border_red : ""
                }`}
                style={{ color: shellTextColor, backgroundColor: shellBkColor }}
            >
                <div
                    className={`${classes.input_container_inner} ${shellClasses.shell_inner}`}
                    style={{ color: shellTextColor }}
                >
                    {label && (
                        <div className={`${classes.label} label`} style={{ color: shellTextColor }}>
                            {label}
                        </div>
                    )}
                    <div className={`${classes.selected} ${shellClasses.shell_selected} selected`} style={{ color: shellTextColor }}>
                        {shellLabel}
                    </div>
                    {!isSelectDisabled && !isLoading && (
                        <button
                            type="button"
                            className={shellClasses.edit_btn}
                            onClick={(event) => {
                                event.stopPropagation();
                                enterEdit();
                            }}
                            aria-label="Edit selection"
                        >
                            <i className="ti ti-chevron-down" aria-hidden />
                        </button>
                    )}
                    {isLoading && (
                        <div className={`select_pointer ${classes.pointer}`}>
                            <span className="loader dark" />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    let content = shell;
    if (hoverMessage !== undefined) {
        content = (
            <WorkQHoverHint message={hoverMessage} disabled={hoverDisabled}>
                {shell}
            </WorkQHoverHint>
        );
    }

    if (editing) {
        const editorOptions = resolvedOptions;
        return (
            <div data-workq-select-open="true">
            <OutsideClickComponent task={exitEdit}>
                <Suspense
                    fallback={
                        <div className={classes.same_size_as_container} style={{ minWidth: minWidth ? minWidth : "", width: "100%" }}>
                            <span className="loader dark" style={{ display: "inline-block", minHeight: 24, minWidth: 24 }} />
                        </div>
                    }
                >
                    <LazyCustomSelect
                        {...restSelectProps}
                        label={label}
                        undetermined={undetermined}
                        isMismatched={isMismatched}
                        minWidth={minWidth}
                        isDisabled={isDisabled}
                        isLoading={isLoading}
                        identifier={identifier}
                        thin_select={thin_select}
                        bkTransparent={bkTransparent}
                        borderRed={borderRed}
                        optionsData={editorOptions}
                        preSelectedValue={preSelectedValue}
                        fallbackSelectedOption={fallbackSelectedOption}
                        action={wrappedAction}
                        focusOnMount={true}
                        onRequestClose={exitEdit}
                    />
                </Suspense>
            </OutsideClickComponent>
            </div>
        );
    }

    return content;
}

export default memo(WorkQDeferredCustomSelect);
